/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2018, Zumper
 * @description  Accepts a browserLocation, city and hood and updates the redux location to match
 */

import { selectToggleValue } from '@zcode-ui/filters'
import {
  ZOOM_CITY,
  ZOOM_HOOD,
  getBoxFromBounds,
  getCityBoxFromBounds,
  guessMapDimensions,
  selectMapDimensions,
} from '@zcode-ui/map'
import { selectCity } from '@zcode-ui/web-zumper-global-state'
import {
  ZOOM_CLUSTER_FILTER_NOT_APPLICABLE,
  boxToQueryBounds,
  getFittedBoundsAndZoom,
  isValidBounds,
  isValidCenter,
  similarValues,
} from '@zcode/nr-utils'
import { getCurrentSearchIsLowResults, getCurrentSearchMaxBox } from '../../../../../inspectors'

import { default as getPrecisionFromZoom } from '../../../../../components/main/helpers/getPrecisionFromZoom'
import { synchronizeMapLocation } from '../../../../mainMap/thunks'
import {
  boxChanged,
  centerChanged,
  cityNameChanged,
  clearCityName,
  clearHoodName,
  countryCodeChanged,
  hoodNameChanged,
  stateNameChanged,
  urlChanged,
  zoomChanged,
} from '../actions'
import {
  selectBox,
  selectCenter,
  selectCityName,
  selectCountry,
  selectHoodName,
  selectStateName,
  selectUrl,
  selectZoom,
} from '../selectors'

/**
 *
 * @param {{
 *  city?: import('@zcode/state-type').City | import('@zcode/state-type').MCity,
 *  hood?: import('@zcode/state-type').Neighborhood | import('@zcode/state-type').MNeighborhood,
 *  ignoreLowResultsFlag?: boolean,
 *  loadNextListables?: boolean,
 *  location: object,
 *  minZoom?: number
 * }} props
 */
export const synchronizeCurrentSearchLocation =
  ({ location, city, hood, ignoreLowResultsFlag, minZoom, loadNextListables }) =>
  (dispatch, getState) => {
    // extract information from the given location
    // we will be comparing this information to the current location in state
    const { box, country, lat, lng, url, zoom } = location
    const center = { lat, lng }

    // extract information from current location
    const state = getState()
    const currentBox = selectBox(state)
    const currentCenter = selectCenter(state)
    const currentUrl = selectUrl(state)
    const currentZoom = selectZoom(state)
    const toggleValue = selectToggleValue(state)
    const isFullWidthMap = toggleValue === 'full-map'
    const dimensions = guessMapDimensions(selectMapDimensions(state), isFullWidthMap)
    const cityObj = selectCity(state)

    const precision = getPrecisionFromZoom(zoom || currentZoom)

    const hasValidBox = isValidBounds(box)
    const hasValidCurrentBox = isValidBounds(currentBox)
    const hasValidCenter = isValidCenter(center)
    const shouldUpdateUrl = url !== currentUrl

    // we have to make sure the the given box and zoom will fit in our dimension.
    /// as a result we determine if we need to obtain the fitted box and zoom
    const fittedBox = box
    const hasValidFittedBox = hasValidBox

    // determine what viewport values we need to update
    const hasDifferentFittedBox =
      hasValidCurrentBox && hasValidFittedBox && !similarValues(currentBox, fittedBox, precision)

    // NOTE: the box is not always in the URL. When the box is missing we need to fetch it with a geos
    // call (which is handled below). Here we check if we have a box in the browserLocation.
    const shouldUpdateBox = (!hasValidCurrentBox && hasValidFittedBox) || hasDifferentFittedBox
    const shouldUpdateCenter =
      hasValidCenter && hasValidFittedBox && !similarValues(currentCenter, center, precision)
    const shouldUpdateZoom = shouldUpdateBox && zoom !== currentZoom

    if (shouldUpdateBox) {
      dispatch(boxChanged(fittedBox))
    }
    if (shouldUpdateCenter) {
      dispatch(centerChanged(center))
    }
    if (shouldUpdateZoom) {
      dispatch(zoomChanged(zoom))
    }
    if (shouldUpdateUrl) {
      dispatch(urlChanged(url ?? null))
    }
    // if we have valid box in the url then we should update the map
    if (hasValidBox) {
      dispatch(
        synchronizeMapLocation({
          box: fittedBox,
          center,
          loadNextListables,
          zoom,
          shouldFitBox: false,
        })
      )
    }

    // Here we received a city and/or hood from a geos call. Notice that the hood is optional.
    if (city) {
      const currentCityName = selectCityName(state)
      const currentHoodName = selectHoodName(state)
      const currentStateName = selectStateName(state)
      const currentCountry = selectCountry(state)
      const isLowResults = getCurrentSearchIsLowResults(state)
      const maxBox = getCurrentSearchMaxBox(state)

      let geoBox = isLowResults && maxBox ? maxBox : hood ? hood.box : city.box
      const geoCenter =
        isLowResults && maxBox
          ? {
              lat: (maxBox.minLat + maxBox.maxLat) / 2,
              lng: (maxBox.minLng + maxBox.maxLng) / 2,
            }
          : hood
            ? { lat: hood.lat, lng: hood.lng }
            : { lat: city.lat, lng: city.lng }
      const geoCityName = city.name
      const geoHoodName = hood ? hood.name : undefined
      const geoStateName = city.state

      // NOTE: the box in a city or hood is an array, not a BoundsRecordType. Here we convert it
      if (Array.isArray(geoBox)) {
        geoBox = boxToQueryBounds(geoBox)
      }
      const hasValidGeoBox = isValidBounds(geoBox)
      const hasValidHood = hood && hood.path === url

      // Ensure that the cityName, hoodName and stateName are correct
      if (geoCityName !== currentCityName) {
        if (geoCityName) {
          dispatch(cityNameChanged(geoCityName))
        } else if (currentCityName && !geoCityName) {
          dispatch(clearCityName())
        }
      }
      if (geoHoodName !== currentHoodName) {
        if (geoHoodName && hasValidHood) {
          dispatch(hoodNameChanged(geoHoodName))
        } else if (currentHoodName && (!geoHoodName || !hasValidHood)) {
          dispatch(clearHoodName())
        }
      }
      if (geoStateName !== currentStateName) {
        dispatch(stateNameChanged(geoStateName))
      }
      if (country !== currentCountry) {
        if (country) {
          dispatch(countryCodeChanged(country))
        }
      }

      // when the box is missing from the url, use the box/center/zoom from the city/hood
      const shouldUseGeoBox =
        !hasValidBox && hasValidGeoBox && (!hasValidCurrentBox || shouldUpdateUrl)
      if (shouldUseGeoBox || (isLowResults && maxBox && !ignoreLowResultsFlag)) {
        const defaultZoom = geoHoodName ? ZOOM_HOOD : ZOOM_CITY

        let box
        if (isLowResults && maxBox) {
          box = maxBox
        } else if (hood) {
          const calculatedHoodBox = getBoxFromBounds(hood)
          box = calculatedHoodBox || geoBox
        } else if (geoCityName) {
          const cityBounds = cityObj && cityObj.displayBounds
          const cityCoordinates = cityBounds?.coordinates
          const calculatedCityBox = cityCoordinates
            ? getCityBoxFromBounds(cityCoordinates)
            : undefined
          box = calculatedCityBox || geoBox
        }

        // fit the geoBox to the map
        const { zoom: fittedGeoZoom } = getFittedBoundsAndZoom({
          allowFloat: true,
          box,
          center: geoCenter,
          defaultZoom,
          dimensions,
          minZoom,
        })

        let newZoom = fittedGeoZoom - 0.5

        if (geoCityName && !hood && newZoom < ZOOM_CLUSTER_FILTER_NOT_APPLICABLE) {
          newZoom = ZOOM_CLUSTER_FILTER_NOT_APPLICABLE + 0.1
        }

        dispatch(boxChanged(box))
        dispatch(centerChanged(geoCenter))
        !loadNextListables && dispatch(zoomChanged(newZoom))

        dispatch(
          synchronizeMapLocation({
            box,
            center: geoCenter,
            loadNextListables,
            zoom: newZoom,
            shouldFitBox: false,
          })
        )
      }
    }
  }
